package ai.jessie.web.profile

import ai.jessie.shared.profile.OwnProfileResponse
import ai.jessie.shared.profile.ProfileErrorResponse
import ai.jessie.shared.profile.ProfileReviewResponse
import ai.jessie.web.onboarding.HTTP_BAD_REQUEST
import ai.jessie.web.onboarding.HTTP_CONFLICT
import ai.jessie.web.onboarding.OnboardingProblem
import ai.jessie.web.onboarding.OnboardingRequestFailure
import ai.jessie.web.onboarding.problem
import ai.jessie.web.onboarding.unavailableProblem
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.currentCoroutineContext
import kotlinx.coroutines.ensureActive
import kotlinx.coroutines.launch

data class ProfileUiState(
    val current: OwnProfileResponse? = null,
    val draft: ProfileDraft? = null,
    val review: ProfileReviewResponse? = null,
    val conflicts: List<ProfileConflict> = emptyList(),
    val busy: Boolean = false,
    val error: OnboardingProblem? = null,
    val problems: Map<String, String> = emptyMap(),
    val notice: String? = null,
)

data class ProfileActions(
    val refresh: () -> Unit,
    val edit: () -> Unit,
    val change: (String, String) -> Unit,
    val review: () -> Unit,
    val confirm: () -> Unit,
    val cancel: () -> Unit,
    val revise: () -> Unit,
)

/**
 * UC-026 own profile. [organisationId] is the routed selection or null (A2); the page keys this
 * state by it, so a changed selection discards membership values, proposals and late responses.
 */
class ProfileState(
    private val port: ProfilePort,
    private val scope: CoroutineScope,
    private val organisationId: String?,
) {
    var state by mutableStateOf(ProfileUiState())
        private set

    val actions =
        ProfileActions(
            refresh = ::refresh,
            edit = {
                val current = state.current
                if (!state.busy && current != null)
                    state = state.copy(draft = current.draft(), notice = null)
            },
            change = { name, value ->
                val draft = state.draft
                if (!state.busy && draft != null && state.review == null)
                    state = state.copy(draft = ProfileDraft(draft.fields + (name to value)))
            },
            review = ::review,
            confirm = ::confirm,
            // A7: nothing was sent for storage, so discarding is purely local.
            cancel = {
                if (!state.busy)
                    state =
                        ProfileUiState(
                            current = state.current,
                            notice = "Abgebrochen. Es wurde nichts gespeichert.",
                        )
            },
            revise = { if (!state.busy) state = state.copy(review = null) },
        )

    fun refresh() {
        if (state.draft != null) return
        request {
            val current = port.read(organisationId).checked()
            currentCoroutineContext().ensureActive()
            state = state.copy(current = current)
        }
    }

    private fun review() {
        val current = state.current
        val draft = state.draft
        if (state.busy || current == null || draft == null) return
        val proposal = draft.proposal(current)
        if (proposal.global == null && proposal.membership == null) {
            state =
                state.copy(
                    error =
                        OnboardingProblem("Du hast nichts geändert. Es gibt nichts zu speichern.")
                )
            return
        }
        request {
            val review = port.review(proposal)
            currentCoroutineContext().ensureActive()
            state = state.copy(review = review)
        }
    }

    private fun confirm() {
        // The server's normalised proposal is confirmed, exactly as it was shown for review.
        val proposal = state.review?.proposal ?: return
        request {
            val saved = port.confirm(proposal)
            currentCoroutineContext().ensureActive()
            state =
                ProfileUiState(
                    current = saved.takeIf { organisationId == null || it.membership != null },
                    busy = true,
                    notice = "Profil gespeichert.",
                )
            // A global-only save answers without the membership; show the selected scope again.
            if (state.current == null) {
                val current = port.read(organisationId).checked()
                currentCoroutineContext().ensureActive()
                state = state.copy(current = current)
            }
        }
    }

    // A response for another organisation than the routed one is never displayed.
    private fun OwnProfileResponse.checked() = also {
        require(it.membership?.organisationId.equals(organisationId, ignoreCase = true))
    }

    // Single-flight; a disposed page scope cannot publish late success, error or finalisation.
    private fun request(block: suspend () -> Unit) {
        if (state.busy) return
        state = state.copy(busy = true, error = null, problems = emptyMap(), notice = null)
        scope.launch {
            try {
                block()
            } catch (cancelled: CancellationException) {
                throw cancelled
            } catch (failure: ProfileRequestFailure) {
                currentCoroutineContext().ensureActive()
                rejected(failure)
            } catch (_: Exception) {
                currentCoroutineContext().ensureActive()
                // Only an unanswered confirmation leaves the stored state unknown.
                val error = if (state.review != null) uncertainProblem() else unavailableProblem()
                state = state.copy(review = null, error = error)
            } finally {
                currentCoroutineContext().ensureActive()
                state = state.copy(busy = false)
            }
        }
    }

    private fun rejected(failure: ProfileRequestFailure) {
        val detail = failure.detail
        when {
            failure.status == HTTP_CONFLICT && detail.code == "STALE_PROFILE" -> stale(detail)
            failure.status == HTTP_BAD_REQUEST && detail.code == "INVALID_PROFILE" -> {
                val problems =
                    detail.fields.filter { it in profileLabels }.associateWith { correction(it) }
                state =
                    state.copy(review = null, problems = problems, error = invalidProblem(problems))
            }
            failure.status == HTTP_BAD_REQUEST || failure.status >= HTTP_SERVER_ERROR ->
                state = state.copy(review = null, error = failure.common().problem())
            // A6 and lost sessions: nothing own or foreign stays on screen.
            else -> state = ProfileUiState(busy = true, error = deniedProblem(failure))
        }
    }

    private fun deniedProblem(failure: ProfileRequestFailure) =
        if (failure.status == HTTP_NOT_FOUND)
            OnboardingProblem(
                "Nicht verfügbar oder du hast keinen Zugriff. Es wurde nichts gespeichert.",
                destination = "/profile".takeIf { organisationId != null },
                destinationLabel = "Profil ohne Organisation öffnen",
            )
        else failure.common().problem()

    private fun stale(detail: ProfileErrorResponse) {
        // A global-only proposal is answered without the membership; its loaded, unproposed
        // values stay. A lost membership arrives as the non-disclosing 404 instead.
        val current =
            detail.current?.let { it.copy(membership = it.membership ?: state.current?.membership) }
        val intended = state.draft
        if (current == null || intended == null) {
            state =
                ProfileUiState(
                    busy = true,
                    error = OnboardingProblem("Nicht verfügbar oder du hast keinen Zugriff."),
                )
            return
        }
        state =
            state.copy(
                current = current,
                draft = current.draft(),
                review = null,
                conflicts = profileConflicts(detail.fields, current, intended),
                error =
                    OnboardingProblem(
                        "Dein Profil wurde inzwischen geändert. Nichts aus deinem Vorschlag wurde " +
                            "gespeichert. Prüfe den Vergleich und trage gewünschte Änderungen erneut ein."
                    ),
            )
    }
}

private fun ProfileRequestFailure.common() =
    OnboardingRequestFailure(status, detail.code, detail.fields)

private fun invalidProblem(problems: Map<String, String>) =
    OnboardingProblem(
        "Bitte korrigiere die markierten Felder. Es wurde nichts gespeichert.",
        problems.keys.map { "profile-$it" },
    )

private fun uncertainProblem() =
    OnboardingProblem(
        "Die Antwort konnte nicht bestätigt werden. Bitte brich die Bearbeitung ab und lade neu, " +
            "um den gespeicherten Stand zu prüfen."
    )

private const val HTTP_NOT_FOUND = 404
private const val HTTP_SERVER_ERROR = 500
